import {
  FormControl,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  RadioGroupProps,
  Skeleton,
  styled,
} from '@mui/material';
import { useController } from 'react-hook-form';
import { ReactNode, useId } from 'react';
import { useFieldStateError } from '../Form/useFieldStateError';
import { Repeat } from '../Utility/Repeat';

export type DesignFieldProps = RadioGroupProps & {
  name: string;
  label?: ReactNode;
  options: Array<{ id: string; backgroundColor: string }> | undefined;
  loading?: boolean;
  required?: boolean;
  disabled?: boolean;
};

const StyledRatio = styled(Radio)(({ theme }) => ({
  width: 40,
  height: 40,
  '&.Mui-checked': { outline: `2px solid ${theme.palette.tertiary.dark}`, outlineOffset: 2 },
  '& .MuiSvgIcon-root': { display: 'none' },
}));

export function DesignField({
  name,
  label,
  options,
  loading,
  required,
  disabled,
  ...props
}: DesignFieldProps) {
  const id = useId();
  const { field, fieldState } = useController({
    name,
    disabled,
    rules: {
      required,
    },
  });

  const fieldError = useFieldStateError(label, fieldState.error);

  return (
    <FormControl>
      {label ? <FormLabel id={id}>{label}</FormLabel> : null}

      <RadioGroup
        {...props}
        row
        sx={{ gap: 2, justifyContent: 'center' }}
        aria-labelledby={id}
        onBlur={field.onBlur}
        onChange={field.onChange}
      >
        {loading ? (
          <Repeat times={4}>
            <Skeleton variant="circular" width={40} height={40} />
          </Repeat>
        ) : (
          options?.map((option) => (
            <StyledRatio
              key={option.id}
              value={option.id}
              checked={field.value === option.id}
              disabled={field.disabled}
              sx={{
                backgroundColor: option.backgroundColor,
                '&:hover': { backgroundColor: option.backgroundColor },
              }}
            />
          ))
        )}
      </RadioGroup>
      {fieldError ? <FormHelperText error>{fieldError}</FormHelperText> : null}
    </FormControl>
  );
}
